import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchGeoCities,
  fetchGeoPincodes,
  submitVendorOnboarding,
  updateVendorOnboarding,
  validateVendorGst,
} from 'services/vendorOnboarding/api';
import {
  CREATE_INITIAL_FORM,
  GST_EXEMPT_TYPE_ID,
  YES_NO_OPTIONS,
  mapCurrencyOptions,
  mapLookupOptions,
  mapStateOptions,
} from './constants';
import {
  isGstExempt,
  validateGstPanState,
  validateVendorOnboardingFieldLengths,
  VENDOR_ADDRESS_MAX_LENGTH,
  VENDOR_BENEFICIARY_NAME_MAX_LENGTH,
  validateFieldMaxLength,
} from './validation';
import {
  buildSubmitFormData,
  extractAttachmentViewUrls,
  extractExistingAttachments,
  formatBankTypeLabel,
  getDefaultNewBankType,
  hasPrimaryBank,
  isPendingNewBankRow,
  vendorToForm,
} from './formData';
import { validateRequiredAttachments } from './attachmentValidation';
import FileUploadField from './components/FileUploadField';
import { FormField, FormSection, SelectInput, TextAreaInput, TextInput } from './components/FormFields';
import Config from 'services/config/config';
import './vendorOnboarding.css';

const MSME_FORM_URL = Config.vendorOnboarding.msmeDeclarationForm;

const getSelectedOrFirst = (list, selectedId) => {
  if (!list?.length) return null;
  if (selectedId) {
    const match = list.find((item) => String(item.id) === String(selectedId));
    if (match) return match;
  }
  return list[0];
};

const clearAddressFields = (prev, { contact_no = '', email = '' } = {}) => ({
  ...prev,
  vendor_address: '',
  vendor_address2: '',
  state_code: '',
  geo_city_id: '',
  geo_pincode_id: '',
  arn_no: '',
  fax_no: '',
  home_page: '',
  contact_person_name: '',
  contact_no,
  gst_no: '',
});

const clearBankFields = (prev) => ({
  ...prev,
  bank_name: '',
  beneficiary_name: '',
  bank_account_no: '',
  ifsc_no: '',
  bank_address: '',
  swift_code: '',
  iban: '',
  currency_code: 'INR',
});

const VendorOnboardingForm = ({ mode, meta }) => {
  const history = useHistory();
  const isEdit = mode === 'edit';

  const [form, setForm] = useState(CREATE_INITIAL_FORM);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [mainError, setMainError] = useState('');
  const [gstValidated, setGstValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidatingGst, setIsValidatingGst] = useState(false);

  const [geoCities, setGeoCities] = useState([]);
  const [geoPincodes, setGeoPincodes] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('');
  const [selectedBankType, setSelectedBankType] = useState('');
  const [isNewAddress, setIsNewAddress] = useState(false);
  const [isNewBank, setIsNewBank] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState({});
  const [attachmentViewUrls, setAttachmentViewUrls] = useState({});
  const [isApprovedVendor, setIsApprovedVendor] = useState(false);

  const lookups = useMemo(() => meta?.lookups || {}, [meta?.lookups]);
  const panRequired = meta?.pan_required !== false;
  const tokenUserId = meta?.token_user_id || null;
  const firmTypes = useMemo(() => mapLookupOptions(lookups.firm_types, ['firm_name', 'name']), [lookups]);
  const gstTypes = useMemo(
    () => mapLookupOptions(lookups.gst_registration_types, ['registration_type', 'name']),
    [lookups]
  );
  const geoStates = useMemo(() => mapStateOptions(lookups.geo_states), [lookups]);
  const currencies = useMemo(() => mapCurrencyOptions(lookups.currency_codes), [lookups]);

  const addresses = useMemo(() => meta?.vendor_addresses ?? [], [meta?.vendor_addresses]);
  const banks = useMemo(() => meta?.vendor_bank_details ?? [], [meta?.vendor_bank_details]);
  const pendingEditRequest = meta?.pending_edit_request;

  const pendingNewBank = useMemo(() => {
    const fromList = banks.find(isPendingNewBankRow);
    if (fromList) return fromList;

    if (!pendingEditRequest?.requested_details) return null;
    if (pendingEditRequest.action === 'add_bank') {
      return pendingEditRequest.requested_details.new_bank ?? pendingEditRequest.requested_details;
    }
    if (pendingEditRequest.action === 'form_update' && pendingEditRequest.requested_details.new_bank) {
      return pendingEditRequest.requested_details.new_bank;
    }
    return null;
  }, [banks, pendingEditRequest]);

  const displayBanks = useMemo(
    () =>
      (banks || []).map((bank) => ({
        ...bank,
        isPending: isPendingNewBankRow(bank),
      })),
    [banks]
  );

  const hasPendingNewBankInList = useMemo(
    () => displayBanks.some((bank) => bank.isPending),
    [displayBanks]
  );

  const defaultNewBankType = useMemo(() => getDefaultNewBankType(banks), [banks]);
  const vendorHasPrimaryBank = useMemo(() => hasPrimaryBank(banks), [banks]);
  const isPendingBankSelected =
    isEdit &&
    (selectedBankId === 'pending-new-bank' ||
      selectedBankId === '0' ||
      displayBanks.some((b) => b.isPending && String(b.id) === String(selectedBankId)));

  const initializedKeyRef = useRef(null);
  const vendorDefaultsRef = useRef({ contact_no: '', email: '' });
  const cancelChequeDefaultsRef = useRef({ attachment: '', viewUrl: '' });
  const [cancelChequeFieldKey, setCancelChequeFieldKey] = useState(0);

  const addressOptions = useMemo(
    () => [
      ...(addresses || []).map((a) => ({
        id: String(a.id),
        name: `${a.name || 'Address'} - ${a.address || ''}`.trim(),
      })),
      { id: 'new', name: 'Add new address' },
    ],
    [addresses]
  );

  const bankOptions = useMemo(
    () => [
      ...(displayBanks || []).map((b) => ({
        id: b.isPending ? String(b.id ?? '0') : String(b.id),
        name: `${b.name || 'Bank'} - ${b.bank_account_no || ''}`.trim(),
      })),
      ...(hasPendingNewBankInList ? [] : [{ id: 'new', name: 'Add new bank' }]),
    ],
    [displayBanks, hasPendingNewBankInList]
  );

  const getFieldLengthError = (key, value) => {
    if (key === 'vendor_address') {
      return validateFieldMaxLength(value, 'Address Line 1', VENDOR_ADDRESS_MAX_LENGTH);
    }
    if (key === 'vendor_address2') {
      return validateFieldMaxLength(value, 'Address Line 2', VENDOR_ADDRESS_MAX_LENGTH);
    }
    if (key === 'beneficiary_name') {
      return validateFieldMaxLength(
        value,
        'Beneficiary Name',
        VENDOR_BENEFICIARY_NAME_MAX_LENGTH
      );
    }
    return '';
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: getFieldLengthError(key, value) }));
    setMainError('');
  };

  const updateFile = (key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const loadCities = useCallback(async (stateId, cityIdToSelect) => {
    if (!stateId) {
      setGeoCities([]);
      setGeoPincodes([]);
      return;
    }
    const res = await fetchGeoCities(stateId);
    const cities = (res.data || []).map((c) => ({
      id: String(c.id),
      name: c.name || c.city_name || String(c.id),
    }));
    setGeoCities(cities);
    if (cityIdToSelect) {
      const pinRes = await fetchGeoPincodes(cityIdToSelect);
      setGeoPincodes(
        (pinRes.data || []).map((p) => ({
          id: String(p.id),
          name: String(p.pincode || p.id),
        }))
      );
    } else {
      setGeoPincodes([]);
    }
  }, []);

  useEffect(() => {
    if (!meta) return;

    const initKey = isEdit
      ? `edit-${meta.vendor?.id ?? ''}`
      : `create-${meta.prefill?.email ?? ''}-${meta.prefill?.name_of_firm ?? ''}`;

    if (initializedKeyRef.current === initKey) return;
    initializedKeyRef.current = initKey;

    if (isEdit && meta.vendor) {
      const selectedAddress = getSelectedOrFirst(
        addresses,
        meta.selected_address_id
      );
      const selectedBank = getSelectedOrFirst(banks, meta.selected_bank_id);
      const initialForm = vendorToForm(meta.vendor, selectedAddress, selectedBank);
      const firmType = (meta.lookups?.firm_types || []).find(
        (f) =>
          String(f.id) === String(meta.vendor.type_of_firm) ||
          f.firm_name === meta.vendor.type_of_firm
      );
      if (firmType) {
        initialForm.type_of_firm = String(firmType.id);
      }
      const gstType = (meta.lookups?.gst_registration_types || []).find(
        (g) =>
          String(g.id) === String(meta.vendor.registration_type_of_gstin) ||
          g.registration_type === meta.vendor.registration_type_of_gstin
      );
      if (gstType) {
        initialForm.registration_type_of_gstin = String(gstType.id);
      }
      setForm({ ...initialForm, remarks: '' });
      setErrors((prev) => ({
        ...prev,
        ...validateVendorOnboardingFieldLengths(initialForm),
      }));
      vendorDefaultsRef.current = {
        contact_no: meta.vendor.contact_no ?? '',
        email: meta.vendor.email ?? '',
      };
      setExistingAttachments(extractExistingAttachments(meta.vendor));
      const viewUrls = extractAttachmentViewUrls(meta.vendor);
      setAttachmentViewUrls(viewUrls);
      cancelChequeDefaultsRef.current = {
        attachment: meta.vendor.cancel_cheque_attachment || '',
        viewUrl: viewUrls.cancel_cheque_attachment || '',
      };
      setIsApprovedVendor(Boolean(meta.is_approved_vendor));
      setSelectedAddressId(String(meta.selected_address_id || selectedAddress?.id || ''));
      setSelectedBankId(String(meta.selected_bank_id || selectedBank?.id || ''));
      setSelectedBankType(
        selectedBank?.type ? String(selectedBank.type).toUpperCase() : ''
      );
      setGstValidated(true);
      loadCities(
        meta.vendor.state_code || selectedAddress?.geo_state_id,
        meta.vendor.geo_city_id || selectedAddress?.geo_city_id
      );
    } else if (!isEdit) {
      const prefill = meta.prefill || {};
      setForm((prev) => ({
        ...prev,
        name_of_firm: prefill.name_of_firm || prev.name_of_firm,
        contact_person_name: prefill.contact_person_name || prev.contact_person_name,
        email: prefill.email || prev.email,
      }));
    }
  }, [meta, isEdit, loadCities, addresses, banks]);

  const selectedState = geoStates.find((s) => String(s.id) === String(form.state_code));

  const showFormSections = isGstExempt(form.registration_type_of_gstin) || gstValidated;

  useEffect(() => {
    if (isGstExempt(form.registration_type_of_gstin)) {
      setGstValidated(true);
    }
  }, [form.registration_type_of_gstin]);

  const handleStateChange = async (stateId) => {
    updateField('state_code', stateId);
    updateField('geo_city_id', '');
    updateField('geo_pincode_id', '');
    await loadCities(stateId);
  };

  const handleCityChange = async (cityId) => {
    updateField('geo_city_id', cityId);
    updateField('geo_pincode_id', '');
    const res = await fetchGeoPincodes(cityId);
    setGeoPincodes(
      (res.data || []).map((p) => ({
        id: String(p.id),
        name: String(p.pincode || p.id),
      }))
    );
  };

  const handleValidateGst = async () => {
    if (!form.gst_no || !form.registration_type_of_gstin) {
      toast.error('Please enter GST registration type and GST number');
      return;
    }
    if (isGstExempt(form.registration_type_of_gstin)) {
      setGstValidated(true);
      return;
    }
    const clientCheck = validateGstPanState({
      gstNo: form.gst_no,
      panNo: form.pan_no,
      stateGstCode: selectedState?.gst_state_code,
    });
    if (!clientCheck.valid) {
      setErrors((prev) => ({ ...prev, gst_no: clientCheck.message }));
      return;
    }
    setIsValidatingGst(true);
    const res = await validateVendorGst(form.gst_no);
    setIsValidatingGst(false);
    if (res.success) {
      setGstValidated(true);
      const tradeName = res.data?.gst_data?.tradeName || res.data?.tradeName;
      if (tradeName && !isEdit) {
        updateField('name_of_firm', tradeName);
      }
      toast.success('GST validated successfully');
    } else {
      setGstValidated(false);
      toast.error(res.message || 'GST validation failed');
    }
  };

  const handleAddressSelect = (id) => {
    if (id === 'new') {
      setIsNewAddress(true);
      setSelectedAddressId('');
      setGeoCities([]);
      setGeoPincodes([]);
      setForm((prev) =>
        clearAddressFields(prev, {
          contact_no: vendorDefaultsRef.current.contact_no || '',
          email: vendorDefaultsRef.current.email || prev.email,
        })
      );
      return;
    }
    setIsNewAddress(false);
    setSelectedAddressId(id);
    const addr = addresses.find((a) => String(a.id) === String(id));
    if (addr) {
      const { contact_no: defaultContactNo, email: defaultEmail } = vendorDefaultsRef.current;
      setForm((prev) => ({
        ...prev,
        vendor_address: addr.address || '',
        vendor_address2: addr.address2 || '',
        state_code: String(addr.geo_state_id || ''),
        geo_city_id: String(addr.geo_city_id || ''),
        geo_pincode_id: String(addr.geo_pincode_id || ''),
        arn_no: addr.arn_no || '',
        fax_no: addr.fax_no || '',
        home_page: addr.home_page || '',
        contact_person_name: addr.name || '',
        contact_no: addr.phone_no || defaultContactNo || '',
        email: addr.email || defaultEmail || '',
        gst_no: addr.gst_no || '',
      }));
      setErrors((prev) => ({
        ...prev,
        vendor_address: validateFieldMaxLength(
          addr.address,
          'Address Line 1',
          VENDOR_ADDRESS_MAX_LENGTH
        ),
        vendor_address2: validateFieldMaxLength(
          addr.address2,
          'Address Line 2',
          VENDOR_ADDRESS_MAX_LENGTH
        ),
      }));
      loadCities(addr.geo_state_id, addr.geo_city_id);
    }
  };

  const refreshCancelChequeAttachment = (clear = true) => {
    if (clear) {
      setFiles((prev) => ({ ...prev, cancel_cheque_attachment: undefined }));
      setExistingAttachments((prev) => ({ ...prev, cancel_cheque_attachment: '' }));
      setAttachmentViewUrls((prev) => ({ ...prev, cancel_cheque_attachment: '' }));
    } else {
      const { attachment, viewUrl } = cancelChequeDefaultsRef.current;
      setFiles((prev) => ({ ...prev, cancel_cheque_attachment: undefined }));
      setExistingAttachments((prev) => ({ ...prev, cancel_cheque_attachment: attachment }));
      setAttachmentViewUrls((prev) => ({ ...prev, cancel_cheque_attachment: viewUrl }));
    }
    setErrors((prev) => ({ ...prev, cancel_cheque_attachment: '' }));
    setCancelChequeFieldKey((key) => key + 1);
  };

  const handleBankSelect = (id) => {
    if (id === 'pending-new-bank' || id === '0') {
      setIsNewBank(false);
      setSelectedBankId(String(id));
      setSelectedBankType(
        pendingNewBank?.type
          ? String(pendingNewBank.type).toUpperCase()
          : getDefaultNewBankType(banks)
      );
      refreshCancelChequeAttachment(false);
      if (pendingNewBank) {
        setForm((prev) => ({
          ...prev,
          bank_name: pendingNewBank.name || '',
          beneficiary_name: pendingNewBank.beneficiary_name || '',
          bank_account_no: pendingNewBank.bank_account_no || '',
          ifsc_no: pendingNewBank.ifsc_code || pendingNewBank.ifsc_no || '',
          bank_address: pendingNewBank.address || pendingNewBank.bank_address || '',
          currency_code: pendingNewBank.currency_code?.code || pendingNewBank.currency_code || prev.currency_code,
          swift_code: pendingNewBank.swift_code || '',
          iban: pendingNewBank.iban || '',
        }));
        setErrors((prev) => ({
          ...prev,
          beneficiary_name: validateFieldMaxLength(
            pendingNewBank.beneficiary_name,
            'Beneficiary Name',
            VENDOR_BENEFICIARY_NAME_MAX_LENGTH
          ),
        }));
      }
      return;
    }
    if (id === 'new') {
      setIsNewBank(true);
      setSelectedBankId('');
      setSelectedBankType('');
      setForm((prev) => clearBankFields(prev));
      refreshCancelChequeAttachment(true);
      return;
    }
    setIsNewBank(false);
    setSelectedBankId(id);
    refreshCancelChequeAttachment(false);
    const bank = banks.find((b) => String(b.id) === String(id));
    if (bank) {
      setSelectedBankType(bank.type ? String(bank.type).toUpperCase() : '');
      setForm((prev) => ({
        ...prev,
        bank_name: bank.name || '',
        beneficiary_name: bank.beneficiary_name || '',
        bank_account_no: bank.bank_account_no || '',
        ifsc_no: bank.ifsc_code || '',
        bank_address: bank.address || '',
        currency_code: bank.currency_code?.code || prev.currency_code,
        swift_code: bank.swift_code || '',
        iban: bank.iban || '',
      }));
      setErrors((prev) => ({
        ...prev,
        beneficiary_name: validateFieldMaxLength(
          bank.beneficiary_name,
          'Beneficiary Name',
          VENDOR_BENEFICIARY_NAME_MAX_LENGTH
        ),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMainError('');

    if (!showFormSections) {
      toast.error('Please validate GST before submitting');
      return;
    }

    const lengthErrors = validateVendorOnboardingFieldLengths(form);
    if (Object.keys(lengthErrors).length) {
      setErrors((prev) => ({ ...prev, ...lengthErrors }));
      toast.error('Please fix validation errors before submitting');
      return;
    }

    const clientGst = validateGstPanState({
      gstNo: form.gst_no,
      panNo: form.pan_no,
      stateGstCode: selectedState?.gst_state_code,
    });
    if (!isGstExempt(form.registration_type_of_gstin) && !clientGst.valid) {
      setErrors((prev) => ({ ...prev, gst_no: clientGst.message }));
      return;
    }

    if (isEdit && isApprovedVendor && !String(form.remarks || '').trim()) {
      setErrors((prev) => ({
        ...prev,
        remarks: 'Remarks is required for approved vendor edit request',
      }));
      toast.error('Please enter remarks');
      return;
    }

    const missingFiles = validateRequiredAttachments({
      form,
      files,
      mode,
      isEdit,
      isNewBank,
      panRequired,
    });
    if (missingFiles.length) {
      const attachmentErrors = {};
      missingFiles.forEach((key) => {
        attachmentErrors[key] = 'This attachment is required';
      });
      setErrors((prev) => ({ ...prev, ...attachmentErrors }));
      toast.error('Please upload all required attachments');
      return;
    }

    const fd = buildSubmitFormData({
      form,
      files,
      mode,
      vendorId: meta?.vendor?.id,
      selectedAddressId: isNewAddress ? '' : selectedAddressId,
      selectedBankId: isNewBank ? '' : selectedBankId,
      selectedBankType: isNewBank ? '' : selectedBankType,
      isNewAddress,
      isNewBank,
      existingAttachments,
      vendorBankDetails: banks,
      tokenUserId,
    });

    setIsSubmitting(true);
    const res = isEdit
      ? await updateVendorOnboarding(fd)
      : await submitVendorOnboarding(fd);
    setIsSubmitting(false);

    if (res.success) {
      history.push('/vendor-onboarding/submitted', {
        isEdit,
        pendingApproval: isEdit && isApprovedVendor,
      });
      return;
    }

    setMainError(res.message || 'Submission failed');
    if (res.errors) {
      const fieldErrors = {};
      Object.entries(res.errors).forEach(([key, val]) => {
        fieldErrors[key] = Array.isArray(val) ? val[0] : val;
      });
      setErrors(fieldErrors);
    }
  };

  const showMsmeAttachment = String(form.msme_reg_no) === '1';
  const showMsmeDeclaration = String(form.msme_reg_no) === '0';
  const showPfAttachment = String(form.pf_reg_no) === '1';
  const showEsicAttachment = String(form.esic_reg_no) === '1';

  return (
    <form className="vendor-onboarding-form" onSubmit={handleSubmit} noValidate>
      <FormSection title="GST Details">
        <FormField label="Registration Type of GSTIN" required error={errors.registration_type_of_gstin}>
          <SelectInput
            value={form.registration_type_of_gstin}
            onChange={(v) => {
              updateField('registration_type_of_gstin', v);
              if (String(v) === GST_EXEMPT_TYPE_ID) {
                updateField('gst_no', '');
                setGstValidated(true);
              } else {
                setGstValidated(false);
              }
            }}
            options={gstTypes}
          />
        </FormField>

        {!isGstExempt(form.registration_type_of_gstin) && (
          <>
            <FormField label="GST No" required error={errors.gst_no}>
              <TextInput
                name="gst_no"
                value={form.gst_no}
                onChange={(v) => {
                  updateField('gst_no', v.toUpperCase());
                  setGstValidated(false);
                }}
              />
            </FormField>
            {!isEdit && (
              <FileUploadField
                label="GST Attachment"
                name="gst_attachment"
                required
                error={errors.gst_attachment}
                selectedFile={files.gst_attachment}
                onChange={updateFile}
              />
            )}
            {isEdit && (
              <FileUploadField
                label="GST Attachment"
                name="gst_attachment"
                error={errors.gst_attachment}
                existingUrl={attachmentViewUrls.gst_attachment}
                selectedFile={files.gst_attachment}
                onChange={updateFile}
              />
            )}
            <small className="vo-hint block">
              GST number must match PAN and state in the address section.
            </small>
            <div className="vo-form-actions">
              <button
                type="button"
                className="vo-btn vo-btn-primary"
                onClick={handleValidateGst}
                disabled={isValidatingGst}
              >
                {isValidatingGst ? 'Validating...' : 'Validate'}
              </button>
            </div>
          </>
        )}
      </FormSection>

      {showFormSections && (
        <>
          {isEdit && addresses.length > 0 && (
            <FormSection title="Select Address">
              <FormField label="Address" required>
                <SelectInput
                  value={isNewAddress ? 'new' : selectedAddressId}
                  onChange={handleAddressSelect}
                  options={addressOptions}
                />
              </FormField>
            </FormSection>
          )}

          <FormSection title="Firm Details">
            <FormField label="Name of Firm" required error={errors.name_of_firm}>
              <TextInput
                name="name_of_firm"
                value={form.name_of_firm}
                disabled={!isEdit && Boolean(meta?.prefill?.name_of_firm)}
                onChange={(v) => updateField('name_of_firm', v)}
              />
            </FormField>
            <FormField label="State" required error={errors.state_code}>
              <SelectInput
                value={form.state_code}
                onChange={handleStateChange}
                options={geoStates}
              />
            </FormField>
            <FormField label="City" required error={errors.geo_city_id}>
              <SelectInput
                value={form.geo_city_id}
                onChange={handleCityChange}
                options={geoCities}
                placeholder="Select City"
              />
            </FormField>
            <FormField label="Pincode" required error={errors.geo_pincode_id}>
              <SelectInput
                value={form.geo_pincode_id}
                onChange={(v) => updateField('geo_pincode_id', v)}
                options={geoPincodes}
                placeholder="Select Pincode"
              />
            </FormField>
            <FormField label="Address Line 1" required error={errors.vendor_address}>
              <TextInput
                name="vendor_address"
                maxLength={VENDOR_ADDRESS_MAX_LENGTH}
                value={form.vendor_address}
                onChange={(v) => updateField('vendor_address', v)}
              />
            </FormField>
            <FormField label="Address Line 2" error={errors.vendor_address2}>
              <TextInput
                name="vendor_address2"
                maxLength={VENDOR_ADDRESS_MAX_LENGTH}
                value={form.vendor_address2}
                onChange={(v) => updateField('vendor_address2', v)}
              />
            </FormField>
            <FormField label="ARN No">
              <TextInput value={form.arn_no} onChange={(v) => updateField('arn_no', v)} />
            </FormField>
            <FormField label="Fax No">
              <TextInput value={form.fax_no} onChange={(v) => updateField('fax_no', v)} />
            </FormField>
            <FormField label="Home Page">
              <TextInput value={form.home_page} onChange={(v) => updateField('home_page', v)} />
            </FormField>
            <FormField label="Contact Person Name" required error={errors.contact_person_name}>
              <TextInput
                value={form.contact_person_name}
                onChange={(v) => updateField('contact_person_name', v)}
              />
            </FormField>
            <FormField label="Contact No" required error={errors.contact_no}>
              <TextInput
                type="tel"
                maxLength={10}
                value={form.contact_no}
                onChange={(v) => updateField('contact_no', v.replace(/\D/g, '').slice(0, 10))}
              />
            </FormField>
            <FormField label="Email" required error={errors.email}>
              <TextInput
                type="email"
                value={form.email}
                disabled
                onChange={(v) => updateField('email', v)}
              />
            </FormField>
            <FormField label="Type of Firm" required error={errors.type_of_firm}>
              <SelectInput
                value={form.type_of_firm}
                onChange={(v) => updateField('type_of_firm', v)}
                options={firmTypes}
              />
            </FormField>
          </FormSection>

          <FormSection title="PAN Number">
            <FormField label="PAN No" required={panRequired} error={errors.pan_no}>
              <TextInput
                value={form.pan_no}
                onChange={(v) => updateField('pan_no', v.toUpperCase())}
                disabled={isEdit}
              />
            </FormField>
            <FileUploadField
              label="PAN Attachment"
              name="pan_attachment"
              required={!isEdit && panRequired}
              error={errors.pan_attachment}
              existingUrl={isEdit ? attachmentViewUrls.pan_attachment : ''}
              selectedFile={files.pan_attachment}
              onChange={updateFile}
            />
          </FormSection>

          <FormSection
            title="MSME Details"
            extra={
              <a href={MSME_FORM_URL} target="_blank" rel="noreferrer" className="vo-link">
                Download application form
              </a>
            }
          >
            <FormField label="MSME Registered" required error={errors.msme_reg_no}>
              <SelectInput
                value={form.msme_reg_no}
                onChange={(v) => updateField('msme_reg_no', v)}
                options={YES_NO_OPTIONS}
              />
            </FormField>
            {showMsmeAttachment && (
              <>
                <FileUploadField
                  label="MSME Attachment"
                  name="msme_attatchment"
                  required={!isEdit}
                  error={errors.msme_attatchment}
                  existingUrl={attachmentViewUrls.msme_attatchment}
                  selectedFile={files.msme_attatchment}
                  onChange={updateFile}
                />
                <FormField label="MSME Registration No" error={errors.msme_reg}>
                  <TextInput value={form.msme_reg} onChange={(v) => updateField('msme_reg', v)} />
                </FormField>
              </>
            )}
            {showMsmeDeclaration && (
              <FileUploadField
                label="Declaration Attachment"
                name="declaration_for_attatchment"
                required={!isEdit}
                error={errors.declaration_for_attatchment}
                existingUrl={attachmentViewUrls.declaration_for_attatchment}
                selectedFile={files.declaration_for_attatchment}
                onChange={updateFile}
              />
            )}
          </FormSection>

          <FormSection title="PF Details">
            <FormField label="PF Registered" required error={errors.pf_reg_no}>
              <SelectInput
                value={form.pf_reg_no}
                onChange={(v) => updateField('pf_reg_no', v)}
                options={YES_NO_OPTIONS}
              />
            </FormField>
            {showPfAttachment && (
              <>
                <FileUploadField
                  label="PF Attachment"
                  name="pf_attatchement"
                  required={!isEdit}
                  error={errors.pf_attatchement}
                  existingUrl={attachmentViewUrls.pf_attatchement}
                  selectedFile={files.pf_attatchement}
                  onChange={updateFile}
                />
                <FormField label="PF Registration No" error={errors.pf_reg}>
                  <TextInput value={form.pf_reg} onChange={(v) => updateField('pf_reg', v)} />
                </FormField>
              </>
            )}
          </FormSection>

          <FormSection title="ESIC Details">
            <FormField label="ESIC Registered" required error={errors.esic_reg_no}>
              <SelectInput
                value={form.esic_reg_no}
                onChange={(v) => updateField('esic_reg_no', v)}
                options={YES_NO_OPTIONS}
              />
            </FormField>
            {showEsicAttachment && (
              <>
                <FileUploadField
                  label="ESIC Attachment"
                  name="esic_attatchemnt"
                  required={!isEdit}
                  error={errors.esic_attatchemnt}
                  existingUrl={attachmentViewUrls.esic_attatchemnt}
                  selectedFile={files.esic_attatchemnt}
                  onChange={updateFile}
                />
                <FormField label="ESIC Registration No" error={errors.esic_reg}>
                  <TextInput value={form.esic_reg} onChange={(v) => updateField('esic_reg', v)} />
                </FormField>
              </>
            )}
          </FormSection>

          {isEdit && displayBanks.length > 0 && (
            <FormSection title="Bank Accounts">
              <ul className="vo-bank-account-list">
                {displayBanks.map((bank) => (
                  <li key={bank.id || bank.bank_account_no} className="vo-bank-account-item">
                    <span className="vo-bank-account-label">
                      {bank.name || 'Bank'}
                      {bank.bank_account_no ? ` - ${bank.bank_account_no}` : ''}
                    </span>
                    {(bank.type || bank.isPending) && (
                      <span className="vo-badge vo-badge-type">
                        {formatBankTypeLabel(
                          bank.isPending ? bank.type || getDefaultNewBankType(banks) : bank.type
                        )}
                      </span>
                    )}
                    {bank.isPending && (
                      <span className="vo-badge vo-badge-pending">Pending finance approval</span>
                    )}
                  </li>
                ))}
              </ul>
            </FormSection>
          )}

          {isEdit && displayBanks.length > 0 && (
            <FormSection title="Select Bank">
              <FormField label="Bank Account">
                <SelectInput
                  value={isNewBank ? 'new' : selectedBankId}
                  onChange={handleBankSelect}
                  options={bankOptions}
                />
              </FormField>
            </FormSection>
          )}

          <FormSection title={isEdit && isNewBank ? 'Add Bank Account' : 'Bank Details'}>
            {isEdit && isNewBank && (
              <>
                <div className="vo-info-banner vo-info-banner--inline">
                  New bank accounts are added as Other when a Primary bank already exists. Finance may
                  promote the new account to Primary during approval.
                </div>
                <p className="vo-hint block">
                  This account will be added as:{' '}
                  <strong>{formatBankTypeLabel(defaultNewBankType)}</strong>
                  {vendorHasPrimaryBank
                    ? ' because a Primary bank already exists.'
                    : ' as the first bank account for this vendor.'}
                </p>
              </>
            )}
            {isEdit && isPendingBankSelected && (
              <div className="vo-info-banner vo-info-banner--inline">
                This bank account is pending finance approval and cannot be edited here.
              </div>
            )}
            <FormField label="Bank Name" required error={errors.bank_name}>
              <TextInput
                value={form.bank_name}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('bank_name', v)}
              />
            </FormField>
            <FormField label="Beneficiary Name" error={errors.beneficiary_name}>
              <TextInput
                maxLength={VENDOR_BENEFICIARY_NAME_MAX_LENGTH}
                value={form.beneficiary_name}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('beneficiary_name', v)}
              />
            </FormField>
            <FormField label="Currency" required error={errors.currency_code}>
              <SelectInput
                value={form.currency_code}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('currency_code', v)}
                options={currencies.length ? currencies : [{ id: 'INR', name: 'INR - Indian Rupee' }]}
              />
            </FormField>
            <FormField label="IFSC Code" required error={errors.ifsc_no}>
              <TextInput
                value={form.ifsc_no}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('ifsc_no', v.toUpperCase())}
              />
            </FormField>
            <FormField label="Bank Address" required error={errors.bank_address}>
              <TextInput
                maxLength={50}
                value={form.bank_address}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('bank_address', v)}
              />
            </FormField>
            <FormField label="Bank Account No" required error={errors.bank_account_no}>
              <TextInput
                value={form.bank_account_no}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('bank_account_no', v)}
              />
            </FormField>
            <FormField label="SWIFT Code">
              <TextInput
                value={form.swift_code}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('swift_code', v)}
              />
            </FormField>
            <FormField label="IBAN">
              <TextInput
                value={form.iban}
                disabled={isPendingBankSelected}
                onChange={(v) => updateField('iban', v)}
              />
            </FormField>
            {!isPendingBankSelected && (
              <FileUploadField
                key={`cancel-cheque-${cancelChequeFieldKey}`}
                label="Cancelled Cheque Attachment"
                name="cancel_cheque_attachment"
                required={!isEdit || isNewBank}
                error={errors.cancel_cheque_attachment}
                existingUrl={attachmentViewUrls.cancel_cheque_attachment}
                selectedFile={files.cancel_cheque_attachment}
                onChange={updateFile}
              />
            )}
          </FormSection>

          {isEdit && isApprovedVendor && (
            <FormSection title="Edit Request">
              <FormField
                label="Remarks"
                required
                error={errors.remarks}
                hint="Enter reason for this edit request"
              >
                <TextAreaInput
                  name="remarks"
                  rows={3}
                  placeholder="Enter reason for edit request"
                  value={form.remarks}
                  onChange={(v) => updateField('remarks', v)}
                />
              </FormField>
            </FormSection>
          )}

          {isEdit && isApprovedVendor && (
            <div className="vo-info-banner">
              Changes to approved vendor details will be sent for finance approval and may not reflect
              immediately.
            </div>
          )}

          {mainError && <div className="vo-main-error">{mainError}</div>}

          <div className="vo-form-btn">
            <button type="submit" className="vo-btn vo-btn-primary vo-btn-lg" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      )}
    </form>
  );
};

export default VendorOnboardingForm;
